/**
 * セキュリティ（05-ui.md §8.10）。
 *
 * - ログイン履歴（コントロールパネル）とセッションの失効
 * - 2 段階認証（TOTP）の登録・リカバリコード
 * - 送信レートの現状（乗っ取られたときに真っ先に出る兆候）
 * - 不審なアクセスがあったときの手順
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CopyableCode,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatCard,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  UsageBar,
  formatBytes,
  formatDateTime,
  formatCount,
} from '@mail-sv/ui';
import { KeyRound, ShieldCheck, Send } from 'lucide-react';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import {
  disableTotp,
  fetchSessions,
  regenerateRecoveryCodes,
  revokeOtherSessions,
  revokeSession,
  setupTotp,
  verifyTotp,
  type TotpSetup,
} from '../lib/auth.js';
import { AppLink } from '../lib/nav.js';
import { LoadingBlock, ErrorAlert } from '../components/async.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { ConfirmDialogButton } from '../components/confirm-button.js';
import { ReauthDialog, needsReauth } from '../components/reauth-dialog.js';
import { HelpNote } from '../components/help.js';
import { useAuth } from '../stores/auth.js';
import { useDomains } from '../lib/hooks.js';
import { formatLimit } from '../lib/format.js';
import type {
  IpBlockAppealCreated,
  IpBlockEntry,
  MfaPolicy,
  SecuritySummary,
  SendingLimits,
} from '../lib/api-types.js';

export function SecurityPage() {
  const summary = useQuery({
    queryKey: qk.securitySummary(),
    queryFn: () => api.get<SecuritySummary>('/security/summary'),
    staleTime: 60_000,
  });

  const limits = useQuery({
    queryKey: qk.sendingLimits(),
    queryFn: () => api.get<SendingLimits>('/security/sending-limits'),
    staleTime: 60_000,
  });

  return (
    <>
      <PageHeader
        title="セキュリティ"
        description="ログイン履歴・2 段階認証・送信制限を確認できます。"
      />

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="迷惑メール遮断（30 日）"
            value={formatCount(summary.data?.spamBlocked30d ?? 0)}
            unit="通"
          />
          <StatCard
            label="ウイルス検出（30 日）"
            value={formatCount(summary.data?.virusBlocked30d ?? 0)}
            unit="通"
            tone={(summary.data?.virusBlocked30d ?? 0) > 0 ? 'destructive' : 'default'}
          />
          <StatCard
            label="ログイン失敗（24 時間）"
            value={formatCount(summary.data?.authFailures24h ?? 0)}
            unit="回"
            tone={(summary.data?.authFailures24h ?? 0) > 50 ? 'warning' : 'default'}
          />
          <StatCard
            label="遮断中の IP"
            value={formatCount(summary.data?.blockedIps ?? 0)}
            unit="件"
          />
        </div>

        <IpBlockSection />

        <MailboxEmergencySection />

        <Section title="送信制限の状況" description="乗っ取り被害を防ぐため、送信数には上限があります。">
          <Card>
            <CardContent className="space-y-4 p-5">
              {limits.isPending ? (
                <LoadingBlock lines={2} />
              ) : limits.isError ? (
                <ErrorAlert error={limits.error} onRetry={() => limits.refetch()} />
              ) : limits.data ? (
                <>
                  <UsageBar
                    used={limits.data.sentToday}
                    total={limits.data.perDayMax}
                    label="直近 24 時間の送信数"
                    formatValue={(n) => `${formatCount(n)} 通`}
                  />
                  <UsageBar
                    used={limits.data.sentThisHour}
                    total={limits.data.perHourMax}
                    label="直近 1 時間の送信数"
                    formatValue={(n) => `${formatCount(n)} 通`}
                  />
                  <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-muted-foreground">1 通あたりの宛先数の上限</dt>
                      <dd className="tabular">{formatLimit(limits.data.recipientsPerMessageMax, ' 件')}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">1 通あたりの最大サイズ</dt>
                      <dd className="tabular">{formatBytes(limits.data.messageSizeBytesMax)}</dd>
                    </div>
                  </dl>
                  <p className="text-xs text-muted-foreground">
                    <Send className="mr-1 inline size-3.5" aria-hidden />
                    上限に達すると、一時的に送信できなくなります。大量送信が必要な場合はプラン変更をご検討ください。
                  </p>
                </>
              ) : null}
            </CardContent>
          </Card>
        </Section>

        <TotpSection />

        <MfaPolicySection />

        <Section title="コントロールパネルのログイン履歴">
          <Card>
            <CardContent className="p-0">
              {summary.isPending ? (
                <div className="p-4">
                  <LoadingBlock lines={3} />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead scope="col">日時</TableHead>
                        <TableHead scope="col">IP アドレス</TableHead>
                        <TableHead scope="col" className="hidden md:table-cell">
                          端末
                        </TableHead>
                        <TableHead scope="col" className="w-24">
                          結果
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(summary.data?.recentLogins ?? []).map((l, i) => (
                        <TableRow key={`${l.at}-${i}`}>
                          <TableCell className="tabular text-xs">{formatDateTime(l.at)}</TableCell>
                          <TableCell className="font-mono text-xs">{l.ip}</TableCell>
                          <TableCell className="hidden max-w-[20rem] truncate text-xs text-muted-foreground md:table-cell">
                            {l.userAgent}
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={l.result === 'success' ? 'succeeded' : 'failed'}
                              label={l.result === 'success' ? '成功' : '失敗'}
                              size="sm"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </Section>

        <SessionsSection />

        <Alert variant="warning">
          <AlertTitle>メールアドレスが乗っ取られた可能性があるとき</AlertTitle>
          <AlertDescription className="space-y-2">
            <ol className="list-decimal space-y-1 pl-5">
              <li>「メールの履歴」でログイン失敗・不審な送信が無いか確認します。</li>
              <li>
                該当するメールアドレスのパスワードを変更します（変更すると即座に接続できなくなります）。
                どのアドレスが漏れたか分からない場合は、上の「緊急時の操作」から全アドレスを一度に再設定できます。
              </li>
              <li>メールソフトに新しいパスワードを設定し直します。</li>
              <li>自動応答や転送設定が勝手に変更されていないか確認します。</li>
            </ol>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm">
                <AppLink to="/mailboxes">パスワードを変更する</AppLink>
              </Button>
              <Button asChild variant="outline" size="sm">
                <AppLink to="/logs">ログイン履歴を見る</AppLink>
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- IP 遮断 */

/**
 * 自組織に効いている IP 遮断の一覧と解除申請（R136）。
 *
 * **遮断されている側に、遮断されている事実を見る手段が無かった。**
 * 以前この場所には「遮断されている IP アドレスと状況を添えてサポートへ
 * ご連絡ください」という案内だけがあり、**その IP アドレスを契約者に
 * 見せる画面が製品内のどこにも無かった**。事務所の固定 IP が遮断されると
 * 全員がメールを使えなくなる状況で、自力で状況を確認する手段も
 * 申請フォームも無く、すべてフリーテキストのサポート起票に流れていた。
 *
 * **解除ボタンは置かない。** 総当たりを受けている最中に本人（を騙る攻撃者）が
 * 解除できるなら遮断の意味が無くなるので、申請はサポートの起票として作り、
 * 解除は運営者の操作に残す（API 側の判断）。起票にすると一次対応の期限が
 * 自動で立ち、「申請したのに誰も見ていない」を督促ジョブが拾える。
 */
function IpBlockSection() {
  const [appealing, setAppealing] = React.useState<IpBlockEntry | null>(null);

  const list = useQuery({
    queryKey: qk.ipBlocks(),
    queryFn: () => api.get<{ items: IpBlockEntry[]; total: number }>('/security/ip-blocks'),
    staleTime: 60_000,
    // 権限が無いメンバーには 403。セクションごと出さないので再試行しない。
    retry: false,
  });

  const items = list.data?.items ?? [];

  return (
    <Section
      title="遮断されている接続元"
      description="ログインの失敗が続いた接続元は自動的に遮断されます。多くは時間の経過とともに解除されます。"
    >
      {list.isPending ? (
        <LoadingBlock lines={2} />
      ) : list.isError ? (
        <ErrorAlert error={list.error} onRetry={() => list.refetch()} />
      ) : items.length === 0 ? (
        <Alert variant="success">
          <AlertTitle>遮断されている接続元はありません</AlertTitle>
          <AlertDescription>
            お客様の組織に対して有効な遮断は現在ありません。
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          <Alert variant="warning">
            <AlertTitle>接続できない場合は解除を申請できます</AlertTitle>
            <AlertDescription>
              お客様の事務所などから接続できなくなっている場合は、該当の行から解除を申請してください。
              申請はサポートの受付として起票され、運営が内容を確認のうえ対応します。
            </AlertDescription>
          </Alert>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">対象</TableHead>
                      <TableHead scope="col">理由</TableHead>
                      <TableHead scope="col" className="hidden md:table-cell">
                        対象のサービス
                      </TableHead>
                      <TableHead scope="col" className="hidden lg:table-cell w-40">
                        自動解除
                      </TableHead>
                      <TableHead scope="col" className="w-36" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-xs">{r.cidr}</TableCell>
                        <TableCell className="max-w-[18rem] text-xs">
                          <p className="truncate">{r.reason}</p>
                          <p className="text-muted-foreground">
                            検知 {formatCount(r.hitCount)} 回
                          </p>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                          {r.protocols.length > 0
                            ? r.protocols.map((p) => labelOf(p)).join('、')
                            : 'すべて'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs">
                          {r.expiresAt ? formatDateTime(r.expiresAt) : '自動解除なし'}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setAppealing(r)}
                          >
                            解除を申請する
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {appealing ? (
        <IpBlockAppealDialog entry={appealing} onClose={() => setAppealing(null)} />
      ) : null}
    </Section>
  );
}

function IpBlockAppealDialog({
  entry,
  onClose,
}: {
  entry: IpBlockEntry;
  onClose: () => void;
}) {
  const [reason, setReason] = React.useState('');
  const submit = useMutation({
    mutationFn: () =>
      api.post<IpBlockAppealCreated>(
        `/security/ip-blocks/${encodeURIComponent(entry.id)}/appeal`,
        { reason: reason.trim() },
      ),
  });

  // サーバ側の zod は 10〜2000 文字。往復を 1 回減らすためだけに前段で見る。
  const tooShort = reason.trim().length < 10;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (submit.isPending) return;
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>遮断の解除を申請する</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{entry.cidr}</span> の遮断について、
            解除をご希望の理由をお知らせください。
          </DialogDescription>
        </DialogHeader>

        {submit.isSuccess ? (
          <>
            <Alert variant="success">
              <AlertTitle>申請を受け付けました</AlertTitle>
              <AlertDescription className="space-y-1">
                <p>
                  受付番号 <span className="font-mono">{submit.data.reference}</span> で起票しました。
                </p>
                <p>
                  {`初回のご回答は ${formatDateTime(submit.data.firstResponseDueAt)} までを目安にご連絡します。`}
                </p>
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                閉じる
              </Button>
              <Button asChild>
                <AppLink to={`/support/tickets/${submit.data.ticketId}`}>
                  やり取りを見る
                </AppLink>
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!tooShort) submit.mutate();
            }}
          >
            <FormAlert error={submit.error} />

            <Field
              label="解除をご希望の理由"
              htmlFor="ipb-reason"
              required
              hint="10 文字以上。心当たりのある操作（社内からの設定ミスなど）や、影響が出ている業務をお書きください。"
            >
              <textarea
                id="ipb-reason"
                rows={5}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="flex w-full rounded-md border border-input bg-panel-elevated px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="社内のメールソフトの設定が誤っており、認証の失敗が続いていました。設定を修正済みです。"
              />
            </Field>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={submit.isPending}
              >
                キャンセル
              </Button>
              <Button type="submit" disabled={tooShort} loading={submit.isPending}>
                申請する
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------ 全アドレスの一括再設定 */

/**
 * 「乗っ取られたかもしれない」ときの一次対応（A23 / A224）。
 *
 * **API（`POST /mailboxes/password-reset-all`）は実装済みなのに、押せる場所が
 * どこにも無かった。** 契約者はアドレス数ぶんだけ詳細画面を開いて 1 件ずつ
 * 押し続けるしかなく、100 アドレスの組織では事実上「やらない」と同じだった。
 *
 * API は **1 回 100 件**で `remaining` を返す設計なので、画面が残件のある
 * あいだ呼び直す（`runAll()`）。生成された平文は**この応答にしか出ない**ので、
 * 画面を閉じると二度と表示できないことを明示し、CSV に落とせるようにする。
 */
interface BulkResetItem {
  mailboxId: string;
  address: string;
  generatedPassword: string;
}

function MailboxEmergencySection() {
  const queryClient = useQueryClient();
  const domains = useDomains();
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [domainId, setDomainId] = React.useState('all');
  const [results, setResults] = React.useState<BulkResetItem[]>([]);

  const run = useMutation({
    mutationFn: async () => {
      /*
       * **残件がある限り呼び直す。** 1 回で 100 件までという上限は
       * argon2id のメモリコストによるもので、繰り返し呼べば必ず全件に
       * 到達する（サーバ側は `password_updated_at` の古い順に処理する）。
       * 上限（50 周 = 5000 件）を置くのは、応答が壊れたときに
       * 無限ループにしないため。
       */
      const collected: BulkResetItem[] = [];
      for (let i = 0; i < 50; i += 1) {
        const res = await api.post<{
          reset: number;
          remaining: boolean;
          items: BulkResetItem[];
        }>('/mailboxes/password-reset-all', {
          reason: reason.trim(),
          confirm: 'RESET-ALL',
          ...(domainId === 'all' ? {} : { domainId }),
        });
        collected.push(...res.items);
        if (!res.remaining) break;
      }
      return collected;
    },
    onSuccess: (items) => {
      setResults(items);
      void queryClient.invalidateQueries({ queryKey: qk.mailboxes() });
    },
  });

  const canRun = reason.trim().length >= 5;

  return (
    <Section
      title="緊急時の操作"
      description="アカウントが乗っ取られた疑いがあるときに、全メールアドレスのパスワードを一度に再設定します。"
    >
      <Card className="border-destructive/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">全アドレスのパスワードを再設定する</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="warning">
            <AlertTitle>実行するとすぐに全員がメールを受け取れなくなります</AlertTitle>
            <AlertDescription>
              対象のすべてのメールアドレスで、いまのパスワードが使えなくなります。
              各利用者のメールソフトに新しいパスワードを設定し直すまで、送受信はできません。
              新しいパスワードは実行直後に 1 度だけ表示されます（あとから確認する方法はありません）。
            </AlertDescription>
          </Alert>
          <Button type="button" variant="destructive" onClick={() => setOpen(true)}>
            <KeyRound aria-hidden />
            一括で再設定する
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (run.isPending) return;
          setOpen(next);
          if (!next) {
            setResults([]);
            setReason('');
            run.reset();
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>全アドレスのパスワードを再設定する</DialogTitle>
            <DialogDescription>
              実行の理由は監査記録と組織へのお知らせに残ります。
            </DialogDescription>
          </DialogHeader>

          {results.length > 0 ? (
            <BulkResetResult items={results} />
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (canRun) run.mutate();
              }}
            >
              <FormAlert error={run.error} />

              <Field label="対象" className="max-w-sm">
                <Select value={domainId} onValueChange={setDomainId}>
                  <SelectTrigger disabled={run.isPending}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">すべてのドメイン</SelectItem>
                    {(domains.data?.items ?? []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field
                label="実行の理由"
                htmlFor="bulk-reason"
                required
                hint="5 文字以上。監査記録と、組織のメンバーへのお知らせに残ります。"
              >
                <Input
                  id="bulk-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="不審なログインを検知したため"
                  disabled={run.isPending}
                />
              </Field>

              <Alert variant="destructive">
                <AlertTitle>この操作は取り消せません</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc space-y-1 pl-4">
                    <li>対象のすべてのメールアドレスで、いまのパスワードが無効になります</li>
                    <li>各利用者のメールソフトを設定し直すまで送受信できません</li>
                    <li>新しいパスワードはこの画面に 1 度だけ表示されます</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={run.isPending}
                >
                  キャンセル
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={!canRun}
                  loading={run.isPending}
                >
                  再設定する
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

/**
 * 生成された平文の一覧。**この画面を閉じると二度と表示できない。**
 *
 * ダウンロードは `Blob` から作る（サーバに平文を再度要求する口は無いし、
 * 作ってもいけない）。
 */
function BulkResetResult({ items }: { items: BulkResetItem[] }) {
  const download = React.useCallback(() => {
    const header = 'address,password\n';
    const body = items
      // CSV に落とすので、生成された値であってもエスケープは省かない。
      .map((i) => `"${i.address.replace(/"/g, '""')}","${i.generatedPassword.replace(/"/g, '""')}"`)
      .join('\n');
    const blob = new Blob([`${header}${body}\n`], { type: 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `mail-passwords-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 10_000);
  }, [items]);

  return (
    <div className="space-y-4">
      <Alert variant="warning">
        <AlertTitle>{`${formatCount(items.length)} 件のパスワードを再設定しました`}</AlertTitle>
        <AlertDescription>
          <strong>この画面を閉じると、新しいパスワードは二度と表示できません。</strong>
          いますぐ書き出して、各利用者へ安全な方法でお知らせください。
        </AlertDescription>
      </Alert>

      <Button type="button" variant="outline" onClick={download}>
        CSV として保存する
      </Button>

      <div className="max-h-72 overflow-y-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">メールアドレス</TableHead>
              <TableHead scope="col">新しいパスワード</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.mailboxId}>
                <TableCell className="font-mono text-xs">{i.address}</TableCell>
                <TableCell>
                  <CopyableCode value={i.generatedPassword} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* --------------------------------------------------- 組織の 2 段階認証ポリシー */

/**
 * 組織全体で 2 段階認証を必須にする設定（`PUT /security/mfa-policy`）。
 *
 * **必須化の前に「誰が締め出されるか」を必ず見せる。** 見せずに切り替えられると、
 * 未登録のメンバーが次のリクエストから `MFA_REQUIRED` で弾かれ、
 * しかも解除できるのは「ログインできない人」だけ、という詰み方をする。
 *
 * サーバー側は**操作した本人が 2FA 未登録なら有効化を拒否する**（最後の砦）。
 * 画面でも同じ条件でボタンを押させない。
 */
function MfaPolicySection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const policy = useQuery({
    queryKey: qk.mfaPolicy(),
    queryFn: () => api.get<MfaPolicy>('/security/mfa-policy'),
  });

  const save = useMutation({
    mutationFn: (mfaRequired: boolean) => api.put('/security/mfa-policy', { mfaRequired }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.mfaPolicy() });
    },
  });

  const data = policy.data;
  const selfEnrolled = user?.mfaEnabled ?? false;
  const withoutMfa = (data?.members ?? []).filter((m) => !m.mfaEnabled);

  return (
    <Section
      title="組織全体で 2 段階認証を必須にする"
      description="必須にすると、この組織のメンバー全員が 2 段階認証を登録しないとログインできなくなります。"
    >
      <Card>
        <CardContent className="space-y-4 p-5">
          {policy.isPending ? (
            <LoadingBlock lines={2} />
          ) : policy.isError ? (
            <ErrorAlert error={policy.error} onRetry={() => void policy.refetch()} />
          ) : data ? (
            <>
              <FormAlert error={save.error} />

              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge
                  status={data.mfaRequired ? 'active' : 'disabled'}
                  label={data.mfaRequired ? '必須になっています' : '任意（各自の設定）'}
                  size="sm"
                  dot
                />
                {data.mfaRequiredAt ? (
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(data.mfaRequiredAt)} から
                  </span>
                ) : null}
              </div>

              {!data.mfaRequired && !selfEnrolled ? (
                <Alert variant="warning">
                  <AlertTitle>先にご自身の 2 段階認証を登録してください</AlertTitle>
                  <AlertDescription>
                    ご自身が未登録のまま必須にすると、<strong>設定を戻せる人が誰もいなくなります</strong>。
                    上の「2 段階認証」から登録してください。
                  </AlertDescription>
                </Alert>
              ) : null}

              {!data.mfaRequired && withoutMfa.length > 0 ? (
                <Alert variant="warning">
                  <AlertTitle>
                    まだ登録していないメンバーが {withoutMfa.length} 名います
                  </AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>
                      必須にすると、次の方は登録を済ませるまでログインできなくなります。
                      先にご連絡のうえ切り替えてください。
                    </p>
                    <ul className="list-disc space-y-0.5 pl-5">
                      {withoutMfa.map((m) => (
                        <li key={m.orgUserId} className="text-xs">
                          {m.displayName || m.email}
                          <span className="ml-1 text-muted-foreground">（{m.email}）</span>
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">メンバー</TableHead>
                      <TableHead scope="col" className="w-40">
                        2 段階認証
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.members.map((m) => (
                      <TableRow key={m.orgUserId}>
                        <TableCell className="text-sm">
                          <p className="font-medium">{m.displayName || m.email}</p>
                          <p className="text-xs text-muted-foreground">{m.email}</p>
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            status={m.mfaEnabled ? 'active' : 'pending'}
                            label={m.mfaEnabled ? '登録済み' : '未登録'}
                            size="sm"
                            dot
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {data.mfaRequired ? (
                <ConfirmDialogButton
                  variant="outline"
                  size="sm"
                  destructive
                  title="2 段階認証の必須化を解除しますか？"
                  description="解除すると、2 段階認証を登録していないメンバーもログインできるようになります。"
                  confirmLabel="解除する"
                  pending={save.isPending}
                  onConfirm={() => save.mutate(false)}
                >
                  必須化を解除する
                </ConfirmDialogButton>
              ) : (
                <ConfirmDialogButton
                  variant="default"
                  size="sm"
                  destructive={false}
                  title="組織全体で 2 段階認証を必須にしますか？"
                  description={
                    withoutMfa.length > 0
                      ? `未登録の ${withoutMfa.length} 名は、登録を済ませるまでログインできなくなります。`
                      : '全員が登録済みです。すぐに切り替えられます。'
                  }
                  confirmLabel="必須にする"
                  pending={save.isPending}
                  disabled={!selfEnrolled}
                  onConfirm={() => save.mutate(true)}
                >
                  <ShieldCheck aria-hidden />
                  必須にする
                </ConfirmDialogButton>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>
    </Section>
  );
}

/* ------------------------------------------------------------ 2 段階認証 */

function TotpSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [setup, setSetup] = React.useState<TotpSetup | null>(null);
  const [code, setCode] = React.useState('');
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[] | null>(null);

  const start = useMutation({
    mutationFn: () => setupTotp(),
    onSuccess: (s) => setSetup(s),
  });

  /**
   * 登録の確定。
   *
   * `verifyTotp()` が**新しいアクセストークンの載せ替えと `/me` の引き直し**まで
   * 行う（`lib/auth.ts`）。ここでキャッシュを捨てるのは、組織の 2FA 必須化で
   * 拒否されていたクエリを新しいトークンで取り直させるため。
   */
  const verify = useMutation({
    mutationFn: () => verifyTotp(code.trim()),
    onSuccess: (r) => {
      setRecoveryCodes(r.recoveryCodes);
      setSetup(null);
      setCode('');
      void queryClient.invalidateQueries();
    },
  });

  const regenerate = useMutation({
    mutationFn: () => regenerateRecoveryCodes(),
    onSuccess: (r) => setRecoveryCodes(r.recoveryCodes),
  });

  const [reauthOpen, setReauthOpen] = React.useState(false);
  const disable = useMutation({
    mutationFn: () => disableTotp(),
    onError: (error) => {
      // 期限切れ（`REAUTH_REQUIRED`）は再認証で解ける。行き止まりにしない。
      if (needsReauth(error)) setReauthOpen(true);
    },
  });

  return (
    <Section
      title="2 段階認証"
      description="パスワードに加えて、スマートフォンの認証アプリのコードを求める設定です。"
    >
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="size-4 text-primary" aria-hidden />
            状態:{' '}
            <Badge variant={user?.mfaEnabled ? 'success' : 'warning'} size="sm">
              {user?.mfaEnabled ? '有効' : '未設定'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {recoveryCodes ? (
            <Alert variant="warning">
              <AlertTitle>リカバリコードを保管してください</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  スマートフォンを紛失したときに使う、1 回限りのコードです。
                  <strong>この画面を閉じると二度と表示できません。</strong>
                </p>
                <CopyableCode value={recoveryCodes.join('\n')} />
                <Button variant="outline" size="sm" onClick={() => setRecoveryCodes(null)}>
                  保管しました
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          {setup ? (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                verify.mutate();
              }}
            >
              <FormAlert error={verify.error} />
              <p className="text-sm text-muted-foreground">
                認証アプリ（Google Authenticator など）で下の QR コードを読み取り、表示された 6 桁の数字を入力してください。
              </p>
              {/*
                QR は API が返す **PNG の data URI**（`qrDataUrl`）を `<img>` で描く。
                以前は `dangerouslySetInnerHTML` で SVG 文字列を挿していたが、
                ① API が返すキーは `qrSvg` ではないので**そもそも出ていなかった**
                ② SVG を innerHTML で入れると中の <script> が動きうる
                の 2 つの問題があった。PNG ならどちらも起きない。
              */}
              <div className="inline-block rounded-lg border border-border bg-white p-3">
                <img src={setup.qrDataUrl} alt="2 要素認証の QR コード" className="w-40" />
              </div>
              <CopyableCode label="手入力用の設定キー" value={setup.secret} />
              <Field label="認証アプリに表示された 6 桁" htmlFor="totp-code" required>
                <Input
                  id="totp-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  className="max-w-[12rem] font-mono tracking-widest"
                />
              </Field>
              <SubmitBar
                submitLabel="有効にする"
                pending={verify.isPending}
                disabled={code.trim().length < 6}
                onCancel={() => setSetup(null)}
              />
            </form>
          ) : (
            <div className="flex flex-wrap gap-2">
              {!user?.mfaEnabled ? (
                <Button onClick={() => start.mutate()} loading={start.isPending}>
                  <KeyRound aria-hidden />
                  2 段階認証を設定する
                </Button>
              ) : (
                <>
                  <ConfirmDialogButton
                    variant="outline"
                    size="default"
                    destructive={false}
                    title="リカバリコードを再発行します"
                    description="これまでのリカバリコードは使えなくなります。新しいコードは 1 度だけ表示されます。"
                    confirmLabel="再発行する"
                    pending={regenerate.isPending}
                    onConfirm={() => regenerate.mutate()}
                  >
                    リカバリコードを再発行
                  </ConfirmDialogButton>
                  {/*
                   * **解除の導線を必ず置く。** 認証アプリを失い、リカバリコードも
                   * 使い切ったとき、解除の口が画面に無いと本人でも復旧できない
                   * （運営へ依頼するしかなくなる）。サーバは fresh-auth を要求
                   * するので、期限切れなら再認証ダイアログを挟む。
                   */}
                  <ConfirmDialogButton
                    variant="ghost"
                    size="default"
                    title="2 段階認証を解除します"
                    description="解除すると、パスワードだけでログインできるようになります。お支払い方法の変更など一部の操作は 2 段階認証が必要なため、実行できなくなります。"
                    confirmLabel="解除する"
                    pending={disable.isPending}
                    onConfirm={() => disable.mutate()}
                  >
                    2 段階認証を解除
                  </ConfirmDialogButton>
                </>
              )}
            </div>
          )}

          {start.isError ? <ErrorAlert error={start.error} /> : null}
          {disable.isError && !needsReauth(disable.error) ? (
            <ErrorAlert error={disable.error} />
          ) : null}
          {disable.isSuccess ? (
            <Alert variant="success">
              <AlertTitle>2 段階認証を解除しました</AlertTitle>
              <AlertDescription>
                安全のため、できるだけ早く再設定してください。
              </AlertDescription>
            </Alert>
          ) : null}

          <ReauthDialog
            open={reauthOpen}
            onOpenChange={setReauthOpen}
            purpose="2 段階認証の解除"
            onSuccess={() => disable.mutate()}
          />

          <HelpNote title="2 段階認証をおすすめする理由">
            パスワードが漏れても、認証アプリのコードが無ければログインできなくなります。
            コントロールパネルにはメールアドレスの作成・削除の権限があるため、設定を強く推奨します。
          </HelpNote>
        </CardContent>
      </Card>
    </Section>
  );
}

/* -------------------------------------------------------------- セッション */

function SessionsSection() {
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: qk.sessions(), queryFn: fetchSessions });

  const revokeOthers = useMutation({
    mutationFn: () => revokeOtherSessions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.sessions() }),
  });

  const revokeOne = useMutation({
    mutationFn: (id: string) => revokeSession(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.sessions() }),
  });

  return (
    /*
      1 行 = **1 回のログイン**。サーバが `family_id` に畳んで返す
      （`routes/auth/common.ts` の `GET /sessions`）。畳む前の生の行は
      「1 本の refresh トークン」で、15 分ごとのローテーションで際限なく
      増えるため、以前はここに数十行が並び「身に覚えのない端末に気付く」
      という画面の目的そのものが失われていた。
    */
    <Section
      title="ログインした端末"
      description="心当たりのない端末があれば、ログアウトさせてください。1 行が 1 回のログインです。"
      actions={
        <ConfirmDialogButton
          variant="outline"
          size="sm"
          destructive={false}
          title="他の端末をすべてログアウトします"
          description="この端末以外のログインをすべて無効にします。他の端末では再度ログインが必要になります。"
          confirmLabel="ログアウトさせる"
          pending={revokeOthers.isPending}
          onConfirm={() => revokeOthers.mutate()}
        >
          他の端末をログアウト
        </ConfirmDialogButton>
      }
    >
      <Card>
        <CardContent className="p-0">
          {sessions.isPending ? (
            <div className="p-4">
              <LoadingBlock lines={2} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">状態</TableHead>
                    <TableHead scope="col">IP アドレス</TableHead>
                    <TableHead scope="col" className="hidden md:table-cell">
                      端末
                    </TableHead>
                    <TableHead scope="col">最終利用</TableHead>
                    <TableHead scope="col" className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(sessions.data ?? []).map((s) => (
                    <TableRow key={s.id}>
                      {/*
                        **状態の列を必ず出す。** 失効済み・期限切れのログインが
                        「ログイン中」に混ざって見えると、利用者は「切ったはずの
                        端末がまだ生きている」と誤解する。
                      */}
                      <TableCell>
                        <Badge variant={s.active ? 'success' : 'muted'} size="sm">
                          {s.active ? '有効' : '終了'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{s.ip}</TableCell>
                      <TableCell className="hidden max-w-[20rem] truncate text-xs text-muted-foreground md:table-cell">
                        {s.userAgent}
                      </TableCell>
                      <TableCell className="tabular text-xs">{formatDateTime(s.lastUsedAt)}</TableCell>
                      <TableCell>
                        {s.current ? (
                          <Badge variant="success" size="sm">
                            この端末
                          </Badge>
                        ) : s.active ? (
                          /*
                           * **1 件ずつ失効させる導線。** 「他の端末をすべて
                           * ログアウト」しか無いと、身に覚えのない 1 台を切るために
                           * 自分の他の端末まで巻き込むことになる。
                           * 既に終了しているログインにはボタンを出さない
                           * （押しても何も起きないボタンは「効かない」と読まれる）。
                           */
                          <ConfirmDialogButton
                            title="この端末をログアウトさせます"
                            description="この端末からのログインを無効にします。再度ログインすれば利用できます。"
                            confirmLabel="ログアウトさせる"
                            pending={revokeOne.isPending}
                            onConfirm={() => revokeOne.mutate(s.id)}
                          >
                            ログアウト
                          </ConfirmDialogButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </Section>
  );
}
